import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

import CustomModal from "../CustomModal";
import AutocompleteInput from "../AutocompleteInput";
import { API } from "../../../utils/api";
import { showToast } from "../../../utils/helper";
import { trackEvent } from "../../../utils/analytics";
import {
  compactVehicleImg,
  standardVehicleImg,
  suvVehicleImg,
} from "../../../Constants/images";
import {
  VEHICLE_MAKES,
  VEHICLE_MAKES_MODELS,
} from "../../../Constants/VehicleMakesModels";

const VEHICLE_TYPES = [
  { value: "compact", label: "Compact", img: compactVehicleImg },
  { value: "standard", label: "Standard", img: standardVehicleImg },
  { value: "suv", label: "SUV", img: suvVehicleImg },
];

const COLOR_SWATCHES = [
  "#000000",
  "#3C70C3",
  "#623A21",
  "#C9C18F",
  "#7C7C89",
  "#D9D9D9",
  "#FFFFFF",
  "#FFCE00",
  "#FF5900",
];

const defaultForm = {
  name: "",
  make: "",
  model: "",
  type: "",
  licensePlate: "",
  color: "",
  status: "published",
};

const buildInitial = (vehicle) => {
  if (!vehicle) return defaultForm;
  return {
    name: vehicle.name || "",
    make: vehicle.make || "",
    model: vehicle.model || "",
    type: vehicle.type || "",
    licensePlate: vehicle.licensePlate || "",
    color: vehicle.color || "",
    status: vehicle.status || "published",
  };
};

const AddVehicleModal = ({ show, onClose, onSuccess, vehicle = null }) => {
  const isEdit = Boolean(vehicle && vehicle.id);
  const [form, setForm] = useState(() => buildInitial(vehicle));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(buildInitial(vehicle));
  }, [vehicle]);

  const onChange = (key, value) => {
    setForm((f) => {
      // Make change clears Model so we don't keep stale Audi A4 under Toyota.
      if (key === "make" && f.make !== value) {
        return { ...f, make: value, model: "" };
      }
      return { ...f, [key]: value };
    });
  };

  const reset = () => setForm(buildInitial(null));

  const handleClose = () => {
    if (saving) return;
    if (!isEdit) reset();
    if (typeof onClose === "function") onClose();
  };

  const validate = () => {
    if (!form.name.trim()) {
      showToast("error", "Please enter your vehicle name");
      return false;
    }
    if (!form.make.trim()) {
      showToast("error", "Please enter your vehicle make");
      return false;
    }
    if (!form.model.trim()) {
      showToast("error", "Please enter your vehicle model");
      return false;
    }
    if (!form.type) {
      showToast("error", "Please choose your vehicle type");
      return false;
    }
    if (!form.licensePlate.trim()) {
      showToast("error", "Please enter your vehicle License Plate");
      return false;
    }
    if (!form.color) {
      showToast("error", "Please choose your vehicle color");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;
    if (!validate()) return;

    setSaving(true);

    const payload = {
      name: form.name.trim(),
      make: form.make.trim(),
      model: form.model.trim(),
      type: form.type,
      licensePlate: form.licensePlate.trim(),
      color: form.color,
      status: form.status || "published",
    };

    const call = isEdit
      ? API.updateVehicle({ id: vehicle.id, ...payload })
      : API.createVehicle(payload);

    call
      .then((res) => {
        if (res && res.type === "success") {
          const saved = res.data || { ...payload, id: vehicle && vehicle.id };
          trackEvent(isEdit ? "web_vehicle_updated" : "web_vehicle_created", {
            vehicle_id: (saved && saved.id) || undefined,
            vehicle_type: payload.type,
          });
          showToast(
            "success",
            res.message || (isEdit ? "Vehicle updated" : "Vehicle added"),
          );
          if (typeof onSuccess === "function") onSuccess(saved);
          if (!isEdit) reset();
          if (typeof onClose === "function") onClose();
        } else if (res && res.message) {
          showToast("error", res.message);
        }
      })
      .catch((err) =>
        showToast(
          "error",
          (err && err.message) ||
            (isEdit ? "Failed to update vehicle" : "Failed to add vehicle"),
        ),
      )
      .finally(() => setSaving(false));
  };

  const makeOptions = VEHICLE_MAKES;
  const modelOptions = VEHICLE_MAKES_MODELS[form.make] || [];

  const title = isEdit ? "Update Vehicle" : "Add Vehicle";
  const submitLabel = isEdit ? "Update Vehicle" : "Add Vehicle";

  return (
    <CustomModal
      show={show}
      title={title}
      onClose={handleClose}
      className="add-vehicle-modal"
    >
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Vehicle Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter name"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            maxLength={50}
          />
        </Form.Group>

        <Row className="mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="fw-bold">Make</Form.Label>
              <AutocompleteInput
                value={form.make}
                onChange={(v) => onChange("make", v)}
                options={makeOptions}
                placeholder="Make"
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="fw-bold">Model</Form.Label>
              <AutocompleteInput
                value={form.model}
                onChange={(v) => onChange("model", v)}
                options={modelOptions}
                placeholder="Model"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Vehicle Type</Form.Label>
          <div className="vehicle-type-row">
            {VEHICLE_TYPES.map((t) => {
              const isActive = form.type === t.value;
              return (
                <div
                  key={`add-vehicle-type-${t.value}`}
                  className="vehicle-type-item"
                  onClick={() => onChange("type", t.value)}
                >
                  <div
                    className={`vehicle-type-tile ${isActive ? "active" : ""}`}
                  >
                    <img src={t.img} alt={t.label} />
                  </div>
                  <p className="vehicle-type-label">{t.label}</p>
                </div>
              );
            })}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">License Plate</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter license plate #"
            value={form.licensePlate}
            onChange={(e) =>
              onChange("licensePlate", e.target.value.toUpperCase())
            }
            maxLength={20}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Color</Form.Label>
          <div className="color-swatch-row">
            {COLOR_SWATCHES.map((c) => {
              const isActive = form.color === c;
              return (
                <div
                  key={`color-${c}`}
                  className={`color-swatch ${isActive ? "active" : ""}`}
                  onClick={() => onChange("color", c)}
                  style={{ background: c }}
                />
              );
            })}
          </div>
        </Form.Group>

        <div className="d-grid">
          <Button
            type="submit"
            variant=""
            disabled={saving}
            className="new-vehicle-submit"
          >
            <span>
              {saving && <i className="fas fa-spin fa-spinner me-2" />}
              {saving ? "Saving..." : submitLabel}
            </span>
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
};

export default AddVehicleModal;
